import { Link } from "react-router-dom";
import { ArrowRight, GraduationCap, Award, Quote } from "lucide-react";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { AboutPortrait } from "@/components/site/AboutPortrait";
import { Reveal } from "@/hooks/useReveal";
import { CREDENTIALS, STATS } from "@/content/site";

// ABOUT_IMAGE — same source portrait as the homepage hero, re-cropped
// through the dedicated `--about-anchor-*` variables so the face and
// orange blazer stay prominent on mobile and desktop.
// Bounded baseline src (see Index.tsx) — never the 1.2 MB original.
import aboutImg from "../assets/dr-sully-hero-blend.jpg?format=jpg&quality=76&w=1024";
import aboutAvifSrcSet from "../assets/dr-sully-hero-blend.jpg?format=avif&quality=60&w=320;360;480;640;768;1024&as=srcset";
import aboutWebpSrcSet from "../assets/dr-sully-hero-blend.jpg?format=webp&quality=70&w=320;360;480;640;768;1024&as=srcset";
import aboutJpgSrcSet from "../assets/dr-sully-hero-blend.jpg?format=jpg&quality=76&w=320;360;480;640;768;1024&as=srcset";
import aboutPlaceholder from "../assets/dr-sully-hero-blend.jpg?format=webp&quality=40&w=24";
import { IMAGE_ALT } from "@/content/image-alt";

const ABOUT_SIZES = "(min-width: 1024px) 480px, (min-width: 640px) 60vw, 92vw";


const About = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageMeta
        title="About Dr. Sully — PsyD · ThD · DMin · CERC"
        description="Royce D. Sullivan — Executive Coach, AI Consultant, Corporate Trainer & Trauma-Informed Leadership Coach. 20+ years across leadership, AI, ministry & community."
        path="/about"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Person",
          "name": "Dr. RD 'Sully' Sullivan",
          "alternateName": ["Dr. Sully", "Royce D. Sullivan"],
          "url": "https://drsullyspeaks.com/about",
          "hasCredential": [
            { "@type": "EducationalOccupationalCredential", "name": "PsyD", "credentialCategory": "degree", "educationalLevel": "Doctorate", "about": "Industrial-Organizational Psychology" },
            { "@type": "EducationalOccupationalCredential", "name": "ThD", "credentialCategory": "degree", "educationalLevel": "Doctorate", "about": "Theology" },
            { "@type": "EducationalOccupationalCredential", "name": "DMin", "credentialCategory": "degree", "educationalLevel": "Doctorate", "about": "Ministry" },
            { "@type": "EducationalOccupationalCredential", "name": "CERC", "credentialCategory": "certification", "about": "Certified Equity and Resiliency Coach" },
          ],
        }}
      />
      <SiteNav />

      {/* HERO */}
      <section className="relative pt-44 md:pt-56 pb-20 bg-hero overflow-hidden">
        <div className="container max-w-4xl text-center">
          <Reveal variant="up">
            <span className="eyebrow !mx-auto">About</span>
            <h1 className="font-display font-bold uppercase text-white text-4xl md:text-6xl lg:text-7xl mt-4 leading-[1.05]">
              Royce D. Sullivan<span className="text-orange">.</span><br />
              <span className="text-gradient-gold">PsyD · ThD · DMin · CERC</span>
            </h1>
            <p className="text-silver text-base md:text-lg mt-6 max-w-3xl mx-auto leading-relaxed">
              Executive Leadership Coach. Lead AI Consultant. Corporate Trainer.
              Trauma-Informed Leadership Coach. Ordained clergy leader with 20+
              years across education, organizational leadership, ministry,
              protective services, healthcare coordination, behavioral
              intervention, nonprofit leadership, and community empowerment.
            </p>
          </Reveal>

          {/* ABOUT_IMAGE */}
          <Reveal variant="up">
            <div className="mt-12 mx-auto w-full max-w-[480px]">
              <AboutPortrait
                src={aboutImg}
                avifSrcSet={aboutAvifSrcSet}
                webpSrcSet={aboutWebpSrcSet}
                jpgSrcSet={aboutJpgSrcSet}
                placeholderSrc={aboutPlaceholder}
                sizes={ABOUT_SIZES}
                priority={false}
                alt={IMAGE_ALT.aboutPortrait}
              />
            </div>
          </Reveal>
        </div>

      </section>

      {/* STATS */}
      <section className="py-16 border-y border-border bg-navy-deep">
        <div className="container max-w-5xl grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border">
          {STATS.map((s) => (
            <div key={s.label} className="bg-navy p-6 text-center">
              <div className="font-display text-gradient-gold text-3xl md:text-4xl">{s.value}</div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-silver mt-2">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* BIO */}
      <section className="py-24 md:py-32">
        <div className="container max-w-3xl space-y-6 text-silver text-base md:text-lg leading-relaxed">
          <Reveal>
            <span className="eyebrow">The Story</span>
            <h2 className="font-display uppercase text-white text-3xl md:text-4xl mt-3 leading-tight">
              Built in the <span className="text-gradient-gold">trenches</span>.
              Sharpened by <span className="text-gradient-gold">scripture</span>.
              Equipped for the <span className="text-gradient-gold">AI era</span>.
            </h2>
          </Reveal>
          <Reveal>
            <p className="mt-6">
              Currently contracted as <span className="text-white">Lead AI Consultant and Executive Leadership Coach for Make Change.AI</span>,
              Dr. Sully also leads <span className="text-white">Organic Coaching & Consulting with DrSullySpeaks</span> and
              serves as <span className="text-white">Pastor of Anchored SA — The San Antonio Community Church</span>.
              His work sits at the intersection of leadership development, AI
              adoption, trauma-informed coaching, and kingdom-focused community
              transformation.
            </p>
          </Reveal>
          <Reveal>
            <p>
              He carries a <span className="text-white">PsyD in Organizational Psychology</span>, a
              <span className="text-white"> Doctor of Theology (ThD)</span>, a
              <span className="text-white"> Doctor of Ministry (DMin)</span>, and is a
              <span className="text-white"> Certified Equity &amp; Resiliency Coach (CERC)</span>.
              That academic depth meets street-level honesty — clarity over
              complexity, accountability over accommodation.
            </p>

          </Reveal>
          <Reveal>
            <p>
              From executives, small businesses, and nonprofits to schools, law
              enforcement professionals, healthcare workers, and faith-based
              organizations, Dr. Sully builds practical systems that help
              leaders and teams improve communication, use AI responsibly,
              resolve conflict, heal trauma, strengthen culture, and execute
              change.
            </p>
          </Reveal>
          <Reveal>
            <div className="mt-8 p-6 border border-gold/30 bg-navy-deep/60 rounded-sm">
              <span className="eyebrow">Signature Framework</span>
              <h3 className="font-display uppercase text-white text-xl md:text-2xl mt-3">
                The <span className="text-gradient-gold">MakeChange</span> Framework
              </h3>
              <p className="text-silver text-sm md:text-base mt-3 leading-relaxed">
                <span className="text-white">M</span>indset ·
                <span className="text-white"> A</span>lignment ·
                <span className="text-white"> K</span>nowledge ·
                <span className="text-white"> E</span>xecution ·
                <span className="text-white"> C</span>ulture ·
                <span className="text-white"> H</span>ealing ·
                <span className="text-white"> A</span>ccountability ·
                <span className="text-white"> N</span>ext Steps ·
                <span className="text-white"> G</span>rowth ·
                <span className="text-white"> E</span>levation
              </p>
              <p className="text-silver/80 text-sm mt-3 leading-relaxed">
                A practical transformation model used to help individuals,
                teams, schools, ministries, small businesses, and nonprofits
                move from confusion to clarity, from conflict to
                accountability, and from intention to measurable action.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SECTORS ENGAGED — PR-grade proof */}
      <section className="py-16 md:py-20 bg-navy border-y border-border">
        <div className="container max-w-5xl">
          <Reveal>
            <div className="text-center mb-10">
              <span className="eyebrow !mx-auto">Engaged Across</span>
              <h2 className="font-display uppercase text-white text-2xl md:text-4xl mt-3 leading-tight">
                Boardrooms. Pulpits. <span className="text-gradient-gold">Classrooms.</span> Capitols.
              </h2>
              <p className="text-silver text-sm md:text-base mt-4 max-w-2xl mx-auto leading-relaxed">
                Trusted by executives, founders, faith leaders, educators, and
                civic officials across five continents. A short list of the
                sectors that have hired Dr. Sully to coach, consult, or keynote:
              </p>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border">
            {[
              "Fortune 500 Operators",
              "Founders & CEOs",
              "Megachurch Leadership",
              "K-12 Superintendents",
              "Civic & Tribal Councils",
              "Nonprofit Boards",
              "Healthcare Systems",
              "AI Product Teams",
            ].map((s) => (
              <div key={s} className="bg-navy-deep p-5 text-center">
                <div className="text-[11px] md:text-xs uppercase tracking-[0.22em] text-silver">{s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CREDENTIALS — grouped */}
      <section className="py-20 md:py-24 bg-navy-deep border-b border-border">
        <div className="container max-w-5xl">
          <Reveal>
            <div className="text-center mb-12">
              <span className="eyebrow !mx-auto"><GraduationCap className="inline h-3 w-3 mr-2" />Credentials</span>
              <h2 className="font-display uppercase text-white text-3xl md:text-5xl mt-3">
                The <span className="text-gradient-gold">receipts</span>.
              </h2>
              <p className="text-silver text-sm md:text-base mt-4 max-w-xl mx-auto leading-relaxed">
                Two doctorates. Six certifications. Two decades of frontline
                leadership work — and a framework leaders still quote after the
                engagement ends.
              </p>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-px bg-border border border-border">
            {[
              {
                label: "Academic",
                items: [
                  "PsyD — Organizational Psychology",
                  "ThD — Doctor of Theology",
                  "DMin — Doctor of Ministry",
                ],
              },
              {
                label: "Certified",
                items: [
                  "CERC — Certified Equity & Resiliency Coach",
                  "Trauma-Informed Leadership Coach",
                  "Community Mediation Specialist",
                  "Lead AI Consultant — Make Change.AI",
                ],
              },

              {
                label: "Frameworks Authored",
                items: [
                  "The MAKECHANGE Framework",
                  "490X — A Movement to Forgiveness",
                  "Brand YOUniversity",
                  "Honest Kingdom Voice (HKV)",
                  "The Make Change Opportunity Scan",
                ],
              },
            ].map((group) => (
              <div key={group.label} className="bg-navy p-6 md:p-7">
                <div className="text-[10px] uppercase tracking-[0.3em] text-gold">{group.label}</div>
                <ul className="mt-5 space-y-3">
                  {group.items.map((c) => (
                    <li key={c} className="flex items-start gap-3 text-white text-sm leading-snug">
                      <Award className="h-4 w-4 text-gold shrink-0 mt-0.5" />
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <Reveal>
            <p className="text-center text-[11px] uppercase tracking-[0.25em] text-silver/70 mt-10">
              Full credential list maintained on file · Available on request for procurement & honoraria
            </p>
          </Reveal>
        </div>
      </section>

      {/* SIGNATURE QUOTE */}
      <section className="py-24 md:py-32">
        <div className="container max-w-3xl text-center">
          <Reveal>
            <Quote className="h-10 w-10 text-gold mx-auto opacity-60" />
            <p className="font-display uppercase text-white text-2xl md:text-4xl mt-6 leading-tight tracking-tight">
              "If it doesn't make <span className="text-gradient-gold">sense</span> —<br />
              make <span className="text-orange">change</span>."
            </p>
            <p className="text-silver text-xs uppercase tracking-[0.3em] mt-6">— Dr. Sully</p>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-32">
        <div className="container max-w-3xl">
          <div className="premium-card p-8 md:p-12 text-center">
            <h2 className="font-display uppercase text-white text-2xl md:text-3xl">
              Ready to <span className="text-gradient-gold">work together?</span>
            </h2>
            <p className="text-silver text-sm md:text-base mt-4 max-w-xl mx-auto">
              Speaking, coaching, consulting, or a custom engagement — start
              with a single conversation.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/speaking" className="inline-flex items-center justify-center gap-2 border border-gold/50 text-white font-display uppercase tracking-[0.2em] text-xs px-6 py-4 hover:bg-gold/10 transition-all">
                Book Dr. Sully to Speak
              </Link>
              <Link to="/inquire" className="inline-flex items-center justify-center gap-2 bg-cta text-white font-display uppercase tracking-[0.2em] text-xs px-6 py-4 shadow-cta hover:brightness-110 transition-all">
                Work With Dr. Sully <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default About;
